import React, { useMemo, useState } from "react";
import {
  alpha,
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import MenuIcon from "@mui/icons-material/Menu";
import MicroscopeIcon from "@mui/icons-material/BiotechOutlined";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import MDButton from "components/MDButton";

const ACCENT = "#0061FE";
/** Shared toolbar control height so search / view / sort / filters align. */
const TOOLBAR_CTRL_H = 40;

/**
 * Shared Chrome for Histology / Embryology catalogue + My Library.
 * Matches the responsive mockup: media toggle, left Categories, search/sort toolbar, main gallery.
 */
export default function CatalogLibraryShell({
  title,
  subtitle,
  darkMode = false,
  segment = "slides", // "slides" | "models"
  onSegmentChange,
  showSegmentToggle = true,
  categories = [], // [{ key, label, count, icon? }]
  selectedCategoryKey = null,
  onCategoryChange,
  allLabel = "All Slides",
  allCount = 0,
  galleryTitle,
  gallerySubtitle,
  searchQuery = "",
  onSearchChange,
  searchPlaceholder = "Search slides, titles or description...",
  sortBy = "newest",
  onSortChange,
  viewMode = "grid",
  onViewModeChange,
  primaryActionLabel,
  onPrimaryAction,
  headerExtra = null,
  toolbarExtra = null,
  children,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);

  const pageBg = darkMode ? "transparent" : "#ffffff";
  // Panels share the page background so padding/gutters don't show a different color.
  const surface = darkMode ? "rgba(255,255,255,0.05)" : "#ffffff";
  const border = darkMode ? "rgba(255,255,255,0.1)" : "rgba(148,163,184,0.18)";
  // Inactive tabs/filters stay very light (not medium grey).
  const mutedBg = darkMode ? "rgba(255,255,255,0.06)" : "#F8FAFC";
  const inactiveBtnBg = darkMode ? "rgba(255,255,255,0.04)" : "#FAFBFC";

  const galleryHeading = galleryTitle || (selectedCategoryKey
    ? categories.find((c) => c.key === selectedCategoryKey)?.label || allLabel
    : allLabel);
  const galleryCount = selectedCategoryKey
    ? categories.find((c) => c.key === selectedCategoryKey)?.count ?? 0
    : allCount;

  const categoryList = useMemo(
    () => [
      { key: null, label: allLabel, count: allCount, icon: FolderOutlinedIcon },
      ...categories,
    ],
    [allLabel, allCount, categories]
  );

  const renderCategoryRow = (item) => {
    const on = selectedCategoryKey === item.key;
    const Icon = item.icon || CategoryOutlinedIcon;
    return (
      <Box
        key={item.key ?? "all"}
        component="button"
        type="button"
        onClick={() => {
          onCategoryChange?.(item.key);
          setMobileCatsOpen(false);
        }}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          border: 0,
          textAlign: "left",
          cursor: "pointer",
          borderRadius: 2.5,
          px: 1.25,
          py: 1.05,
          m: 0,
          font: "inherit",
          bgcolor: on ? alpha(ACCENT, darkMode ? 0.22 : 0.1) : inactiveBtnBg,
          color: on ? ACCENT : theme.palette.text.primary,
          fontWeight: on ? 700 : 550,
          transition: "background 140ms ease, color 140ms ease",
          "&:hover": {
            bgcolor: on ? alpha(ACCENT, darkMode ? 0.28 : 0.14) : mutedBg,
          },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: on ? alpha(ACCENT, 0.16) : inactiveBtnBg,
            color: on ? ACCENT : "text.secondary",
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 14,
          }}
        >
          {item.label}
        </Box>
        <Box
          component="span"
          sx={{
            minWidth: 26,
            height: 22,
            px: 0.75,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11.5,
            fontWeight: 700,
            bgcolor: on ? ACCENT : alpha(ACCENT, 0.12),
            color: on ? "#fff" : ACCENT,
            flexShrink: 0,
          }}
        >
          {item.count}
        </Box>
      </Box>
    );
  };

  const categoriesPanel = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 0.75, mb: 1.5 }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 800, letterSpacing: "0.02em", color: "text.secondary" }}
        >
          Categories
        </Typography>
        {onPrimaryAction ? (
          <Tooltip title={primaryActionLabel || "Add"}>
            <IconButton
              size="small"
              onClick={onPrimaryAction}
              sx={{ color: ACCENT, bgcolor: alpha(ACCENT, 0.08) }}
            >
              <AddRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.35,
          overflowY: "auto",
          pr: 0.25,
          flex: 1,
          minHeight: 0,
        }}
      >
        {categoryList.map(renderCategoryRow)}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: pageBg,
        px: { xs: 1.5, sm: 2, md: 2.5 },
        pt: { xs: 1.5, sm: 2, md: 2.5 },
        pb: { xs: 2, md: 3 },
        boxSizing: "border-box",
        borderRadius: { xs: 2.5, md: 3.5 },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        justifyContent="space-between"
        gap={2}
        flexDirection={{ xs: "column", sm: "row" }}
        flexWrap="wrap"
        sx={{ mb: 2.5 }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 260px" }}>
          <Box display="flex" alignItems="center" gap={1.25} mb={0.6}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(ACCENT, 0.12),
                color: ACCENT,
                flexShrink: 0,
              }}
            >
              <FolderOutlinedIcon />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.02em",
                color: theme.palette.text.primary,
                lineHeight: 1.15,
              }}
            >
              {title}
            </Typography>
          </Box>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640, lineHeight: 1.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {headerExtra}
          {onPrimaryAction && primaryActionLabel ? (
            <MDButton
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={onPrimaryAction}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2.5,
                px: 2.25,
                height: 42,
                color: "#fff !important",
                bgcolor: `${ACCENT} !important`,
                boxShadow: `0 10px 22px ${alpha(ACCENT, 0.28)}`,
                "&:hover": { bgcolor: "#0052d6 !important" },
              }}
            >
              {primaryActionLabel}
            </MDButton>
          ) : null}
        </Box>
      </Box>

      {/* Slides | 3D Models */}
      {showSegmentToggle ? (
        <Box display="flex" justifyContent="center" mb={2.5}>
          <Box
            sx={{
              display: "inline-flex",
              p: 0.55,
              gap: 0.5,
              borderRadius: 999,
              bgcolor: inactiveBtnBg,
              border: "1px solid",
              borderColor: border,
              boxShadow: darkMode ? "none" : "0 8px 22px rgba(15,23,42,0.04)",
              maxWidth: "100%",
            }}
          >
            {[
              { key: "slides", label: "Slides", icon: MicroscopeIcon },
              { key: "models", label: "3D Models", icon: ViewInArIcon },
            ].map((item) => {
              const on = segment === item.key;
              const Icon = item.icon;
              return (
                <Box
                  key={item.key}
                  component="button"
                  type="button"
                  onClick={() => onSegmentChange?.(item.key)}
                  sx={{
                    border: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    bgcolor: on ? ACCENT : "transparent",
                    color: on ? "#fff" : alpha(theme.palette.text.primary, 0.72),
                    borderRadius: 999,
                    minWidth: { xs: 130, sm: 180 },
                    height: 44,
                    px: 2.25,
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: "pointer",
                    boxShadow: on ? `0 8px 18px ${alpha(ACCENT, 0.32)}` : "none",
                    "&:hover": on
                      ? undefined
                      : { bgcolor: mutedBg, color: theme.palette.text.primary },
                  }}
                >
                  <Icon sx={{ fontSize: 20, opacity: on ? 1 : 0.55 }} />
                  {item.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}

      {/* Body: sidebar + main */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(220px, 260px) minmax(0, 1fr)",
          },
          gap: { xs: 1.5, md: 2.25 },
          alignItems: "start",
        }}
      >
        {/* Desktop categories */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "sticky",
            top: 16,
            alignSelf: "start",
            bgcolor: surface,
            border: "1px solid",
            borderColor: border,
            borderRadius: 3.5,
            p: 2,
            pt: 2.25,
            boxShadow: darkMode ? "none" : "0 10px 28px rgba(15,23,42,0.05)",
            maxHeight: "calc(100vh - 120px)",
            overflow: "hidden",
          }}
        >
          {categoriesPanel}
        </Box>

        {/* Main gallery */}
        <Box
          sx={{
            bgcolor: surface,
            border: "1px solid",
            borderColor: border,
            borderRadius: 3.5,
            p: { xs: 1.5, sm: 2, md: 2.5 },
            boxShadow: darkMode ? "none" : "0 10px 28px rgba(15,23,42,0.05)",
            minWidth: 0,
          }}
        >
          {/* Toolbar */}
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            {isMobile ? (
              <MDButton
                variant="outlined"
                color="info"
                startIcon={<MenuIcon />}
                onClick={() => setMobileCatsOpen(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2.5,
                  height: TOOLBAR_CTRL_H,
                  borderColor: `${alpha(ACCENT, 0.35)} !important`,
                  color: `${ACCENT} !important`,
                }}
              >
                Categories
              </MDButton>
            ) : null}

            <TextField
              value={searchQuery}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder={searchPlaceholder}
              size="small"
              sx={{
                flex: "1 1 220px",
                minWidth: { xs: "100%", sm: 200 },
                "& .MuiInputBase-root": {
                  height: TOOLBAR_CTRL_H,
                  borderRadius: 999,
                  bgcolor: darkMode ? "rgba(255,255,255,0.06)" : "#F8FAFC",
                  fontSize: 14.5,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => onSearchChange?.("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {onViewModeChange ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: TOOLBAR_CTRL_H,
                  boxSizing: "border-box",
                  p: 0.4,
                  gap: 0.35,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: border,
                  bgcolor: inactiveBtnBg,
                }}
              >
                {[
                  { key: "grid", icon: GridViewRoundedIcon, label: "Grid" },
                  { key: "list", icon: ViewListRoundedIcon, label: "List" },
                ].map((v) => {
                  const on = viewMode === v.key;
                  const Icon = v.icon;
                  return (
                    <IconButton
                      key={v.key}
                      size="small"
                      aria-label={v.label}
                      aria-pressed={on}
                      onClick={() => onViewModeChange(v.key)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 2,
                        color: on ? "#fff" : alpha(theme.palette.text.secondary, 0.7),
                        bgcolor: on ? ACCENT : "transparent",
                        "&:hover": { bgcolor: on ? ACCENT : mutedBg },
                      }}
                    >
                      <Icon fontSize="small" />
                    </IconButton>
                  );
                })}
              </Box>
            ) : null}

            {onSortChange ? (
              <TextField
                select
                size="small"
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                sx={{
                  minWidth: 168,
                  "& .MuiInputBase-root": {
                    height: TOOLBAR_CTRL_H,
                    borderRadius: 2.5,
                    bgcolor: darkMode ? "rgba(255,255,255,0.04)" : "#F8FAFC",
                    fontSize: 13.5,
                    fontWeight: 600,
                  },
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    py: 0,
                    height: TOOLBAR_CTRL_H,
                    boxSizing: "border-box",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SortRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="newest">Sort by: Newest</MenuItem>
                <MenuItem value="oldest">Sort by: Oldest</MenuItem>
                <MenuItem value="az">Sort by: A–Z</MenuItem>
                <MenuItem value="za">Sort by: Z–A</MenuItem>
              </TextField>
            ) : null}

            {toolbarExtra ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: TOOLBAR_CTRL_H,
                  minHeight: TOOLBAR_CTRL_H,
                }}
              >
                {toolbarExtra}
              </Box>
            ) : null}
          </Box>

          {/* Gallery heading */}
          <Box mb={2}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}>
                {galleryHeading}
              </Typography>
              <Box
                component="span"
                sx={{
                  px: 1.1,
                  py: 0.35,
                  borderRadius: 999,
                  bgcolor: alpha(ACCENT, 0.12),
                  color: ACCENT,
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                {galleryCount}
              </Box>
            </Box>
            {gallerySubtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                {gallerySubtitle}
              </Typography>
            ) : null}
          </Box>

          {children}
        </Box>
      </Box>

      {/* Mobile categories drawer */}
      <Drawer
        anchor="left"
        open={mobileCatsOpen}
        onClose={() => setMobileCatsOpen(false)}
        PaperProps={{
          sx: {
            width: "min(320px, 88vw)",
            p: 2,
            bgcolor: surface,
          },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography fontWeight={800}>Categories</Typography>
          <IconButton onClick={() => setMobileCatsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        {categoriesPanel}
      </Drawer>
    </Box>
  );
}
